import axios from 'axios';
import config from '../config';

/**
 * Safely parse Google Sheets JSONP response
 * @param {string} responseData - Raw response from Google Sheets API
 * @returns {Object} Parsed JSON data
 * @throws {Error} If parsing fails
 */
const parseGoogleSheetsResponse = (responseData) => {
  if (!responseData || typeof responseData !== 'string') {
    throw new Error('Invalid response from Google Sheets');
  }

  // Google Sheets JSONP format: google.visualization.Query.setResponse({...});
  // Find the JSON object between the parentheses
  const startMarker = 'google.visualization.Query.setResponse(';
  const startIndex = responseData.indexOf(startMarker);

  if (startIndex === -1) {
    throw new Error('Unexpected Google Sheets response format');
  }

  const jsonStart = startIndex + startMarker.length;
  const jsonEnd = responseData.lastIndexOf(');');

  if (jsonEnd === -1 || jsonEnd <= jsonStart) {
    throw new Error('Could not find JSON boundaries in response');
  }

  const jsonString = responseData.substring(jsonStart, jsonEnd);

  try {
    return JSON.parse(jsonString);
  } catch (parseError) {
    throw new Error(`Failed to parse Google Sheets JSON: ${parseError.message}`);
  }
};

/**
 * Fetch race data from Google Spreadsheet
 * @returns {Promise<Array>} Array of race objects
 */
export const fetchSpreadsheetData = async () => {
  try {
    // Using Google Sheets API v4 - requires the sheet to be publicly viewable
    const url = `https://docs.google.com/spreadsheets/d/${config.spreadsheetId}/gviz/tq?tqx=out:json`;

    const response = await axios.get(url);

    // Safely parse the JSONP response
    const data = parseGoogleSheetsResponse(response.data);

    // Validate data structure
    if (!data.table || !Array.isArray(data.table.rows)) {
      throw new Error('Invalid data structure from Google Sheets');
    }

    // Parse the data into race objects
    const races = [];
    const rows = data.table.rows;
    
    rows.forEach(row => {
      if (!row.c || !row.c[0]) return; // Skip empty rows
      
      const race = {
        number: row.c[0]?.v || '',
        date: row.c[1]?.f || row.c[1]?.v || '',
        name: row.c[2]?.v || '',
        distance: row.c[3]?.v || '',
        time: row.c[4]?.v || '',
        position: row.c[5]?.v || '',
        elevation: row.c[6]?.v || '',
        video: row.c[7]?.v || '',
        report: row.c[8]?.v || '',
        strava: row.c[9]?.v || '',
        officialResults: row.c[10]?.v || '',
        type: row.c[11]?.v || '', // Marathon or Ultra
        terrain: row.c[12]?.v || '', // Road or Trail
        marathonCount: row.c[13]?.v || '',
        ultraCount: row.c[14]?.v || '',
        roadCount: row.c[15]?.v || '',
        trailCount: row.c[16]?.v || '',
        hundredMilers: row.c[17]?.v || '',
        raceCount: row.c[18]?.v || ''
      };
      
      // Only include races with Strava links
      if (race.strava) {
        races.push(race);
      }
    });
    
    console.log(`Fetched ${races.length} races from spreadsheet`);
    return races;
    
  } catch (error) {
    console.error('Error fetching spreadsheet data:', error);
    throw error;
  }
};

// Re-export extractActivityId for backward compatibility
export { extractActivityId as extractStravaActivityId } from './mapUtils';
